import type { Portal } from "@wakaskul/shared";
import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuthStore } from "../lib/auth-store";
import { landingPath } from "../lib/session";

/**
 * Route-level gate.
 *
 * This decides what a user is *shown*. It decides nothing about what they may
 * *do* — every request is authorised again server-side by CASL abilities, and
 * every row is filtered again by Row-Level Security. A user who types another
 * portal's URL gets its screens and a wall of 403s, which is the correct
 * outcome: the boundary that matters is the one they cannot edit.
 */
export function RequirePortal({ portal, children }: { portal: Portal; children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Every session carries a school from the moment it's issued now — login
  // resolves it from the subdomain, never leaves it unset. Reaching this
  // means something is wrong with the session itself, not that a choice is
  // pending, so back to a fresh login rather than a picker that no longer
  // exists.
  if (!user.activeSchoolId) {
    return <Navigate to="/login" replace />;
  }

  if (!user.portals.includes(portal)) {
    return <Navigate to={landingPath(user)} replace />;
  }

  return <>{children}</>;
}
